import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1607344645866-009c320b63e0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const FeaturesList = styled.ul`
  margin: 2rem 0;
  padding: 0;
  list-style-position: inside;
`;

const FeatureItem = styled.li`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  
  &::before {
    content: '•';
    color: #004744;
    font-weight: bold;
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`;

const NumberedList = styled.ol`
  margin: 2rem 0;
  padding-left: 1.5rem;
`;

const NumberedItem = styled.li`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 4rem;
  text-align: center;
`;

const GiftCard: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Gift Card | J-O-H' : 'Gift Card | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri la Gift Card di J-O-H, il regalo perfetto per accedere ai nostri servizi esclusivi' 
            : 'Discover the J-O-H Gift Card, the perfect gift to access our exclusive services'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/card">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>GIFT CARD</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'La gift card, o carta regalo, è una forma di pagamento prepagato emessa da J-O-H, che può essere utilizzata per effettuare acquisti presso il negozio o servizio specificato sulla carta. Le gift card sono un modo per dare flessibilità nella scelta, permettendo al destinatario di selezionare i prodotti o i servizi che desidera.'
              : 'The gift card is a form of prepaid payment issued by J-O-H, which can be used to make purchases at the store or service specified on the card. Gift cards are a way to give flexibility in choice, allowing the recipient to select the products or services they want.'}
          </Description>
          
          <NumberedList>
            <NumberedItem>
              <strong>{language === 'it' ? 'PREPAGATA' : 'PREPAID'}</strong>: 
              {language === 'it' 
                ? ' La gift card viene acquistata con un certo importo prepagato. Questo importo può poi essere utilizzato per effettuare acquisti fino all\'esaurimento del credito sulla carta.'
                : ' The gift card is purchased with a certain prepaid amount. This amount can then be used to make purchases until the credit on the card is used up.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'VARI UTILIZZI' : 'VARIOUS USES'}</strong>: 
              {language === 'it' 
                ? ' La gift card può essere utilizzata in un\'ampia gamma di spazi, tra cui negozi al dettaglio, ristoranti, catene di caffetterie, centri commerciali, servizi online e altro ancora.'
                : ' The gift card can be used in a wide range of spaces, including retail stores, restaurants, coffee chains, shopping malls, online services and more.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'SCADENZA' : 'EXPIRY'}</strong>: 
              {language === 'it' 
                ? ' La gift card può avere una data di scadenza, il che significa che deve essere utilizzata entro un certo periodo di tempo.'
                : ' The gift card may have an expiration date, which means it must be used within a certain period of time.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'CARTE FISICHE E DIGITALI' : 'PHYSICAL AND DIGITAL CARDS'}</strong>: 
              {language === 'it' 
                ? ' La gift card può essere fisica, simile a una carta di credito, o digitale, con un codice o un numero unico che può essere inserito durante il processo di checkout online.'
                : ' The gift card can be physical, similar to a credit card, or digital, with a unique code or number that can be entered during the online checkout process.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'CONTROLLO DEL SALDO' : 'CHECKING YOUR BALANCE'}</strong>: 
              {language === 'it' 
                ? ' I destinatari possono di solito controllare il saldo rimanente sulla loro gift card online o tramite il servizio clienti del commerciante.'
                : ' Recipients can usually check the remaining balance on their gift card online or through merchant customer service.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'ATTIVAZIONE E REGISTRAZIONE' : 'ACTIVATION AND REGISTRATION'}</strong>: 
              {language === 'it' 
                ? ' La gift card richiede l\'attivazione prima dell\'uso o può essere registrata online per proteggere il credito in caso di smarrimento o furto.'
                : ' The gift card requires activation before use or can be registered online to protect your credit in case of loss or theft.'}
            </NumberedItem>
          </NumberedList>
          
          <Description>
            {language === 'it' 
              ? 'È importante notare che le politiche delle gift card possono variare. Prima di acquistare o utilizzare la gift card, dovresti leggere attentamente i termini e le condizioni specifici associati a quella carta.'
              : 'It is important to note that gift card policies may vary. Before purchasing or using the gift card, you should carefully read the specific terms and conditions associated with that card.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default GiftCard; 